import { useMemo, useState } from "react";
import { assetTypeInfo, searchAssetTypes, type AssetType } from "@klantpunt/shared";
import { AssetBadge } from "./assetIcons";

/**
 * Assettype kiezen ("Wat is het?", docs/SYMBOLEN.md): vaste Klantpunt-lijst met eigen symbolen. Het
 * type bepaalt het symbool; het element (2 cijfers) vult de dialoog zelf in als dat nog leeg is.
 */
export function AssetTypePicker({
  value,
  onChange,
  suggestion = null,
  defaultOpen = false,
}: {
  value: AssetType | null;
  onChange: (t: AssetType | null) => void;
  /** Voorstel uit naam of code (nooit stil opgeslagen). */
  suggestion?: AssetType | null;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const hits = useMemo(() => searchAssetTypes(q), [q]);
  const cur = assetTypeInfo(value);
  const sug = suggestion && suggestion !== value ? assetTypeInfo(suggestion) : undefined;
  const close = () => {
    setOpen(false);
    setQ("");
  };
  const pick = (t: AssetType | null) => {
    onChange(t);
    close();
  };

  return (
    <div className="catpicker" data-testid="asset-type-picker">
      <div className="catcur">
        <AssetBadge code={cur?.element ?? null} type={value} size={36} />
        <span className="grow">
          <strong>{cur?.label ?? "Nog niet gekozen"}</strong>
          <span className="muted small block">
            {cur ? `Symbool en element ${cur.element}` : "Symbool van het element"}
          </span>
        </span>
        {value && !open && (
          <button type="button" className="btn small" onClick={() => onChange(null)}>
            Wissen
          </button>
        )}
        <button
          type="button"
          className="btn small"
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
        >
          {open ? "Inklappen" : value ? "Wijzigen" : "Kiezen"}
        </button>
      </div>
      {sug && (
        <div className="catsuggest" role="group" aria-label="Voorstel">
          <span className="muted small">Voorstel:</span>
          <button type="button" className="chip chipbtn" onClick={() => pick(sug.key)}>
            <AssetBadge code={sug.element} type={sug.key} size={18} />
            {sug.label}
          </button>
        </div>
      )}
      {open && (
        <div className="catlist">
          <input
            type="search"
            aria-label="Zoeken wat het is"
            placeholder="Zoek, bv. ketel, groepenkast of rwa"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="cattiles" role="group" aria-label="Keuzes">
            {hits.map((t) => (
              <button
                key={t.key}
                type="button"
                className="cattile"
                aria-current={t.key === value || undefined}
                onClick={() => pick(t.key)}
              >
                <AssetBadge code={t.element} type={t.key} size={30} />
                <span className="grow">
                  {t.label}
                  <span className="muted small block">element {t.element}</span>
                </span>
              </button>
            ))}
          </div>
          {hits.length === 0 && (
            <p className="muted">Niets gevonden. Kies dan alleen een element; het symbool komt daarvan.</p>
          )}
        </div>
      )}
    </div>
  );
}
